import type Uppy from '@uppy/core';
import type { UploadResult } from '@uppy/core';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { ordersApi } from 'api';
import { useSciDomain } from 'components';
import { EventName, LanguageCode, OrderStatus, QueryKey } from 'enums';
import { useLanguage } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Order, OrderReceipt } from 'types';
import { eventUtils, gaUtils } from 'utils';

import { loadReceiptUppy } from './receiptUppy';

const DEFAULT_UPPY_LANGUAGE = 'en_US';

const MAP_LANGUAGE_TO_UPPY_LOCALE: Record<Partial<LanguageCode>, string> = {
  [LanguageCode.English]: DEFAULT_UPPY_LANGUAGE,
  [LanguageCode.Russian]: 'ru_RU',
  [LanguageCode.Korean]: 'ko_KR',
  [LanguageCode.Spanish]: 'es_ES',
  [LanguageCode.Hindi]: 'hi_IN',
  [LanguageCode.Turkish]: 'tr_TR',
  [LanguageCode.Ukrainian]: 'uk_UA',
  [LanguageCode.Uzbek]: 'uz_UZ',
  [LanguageCode.Armenian]: 'hy_AM',
  [LanguageCode.Azerbaijani]: 'az_AZ',
  [LanguageCode.Kyrgyz]: 'ky_KG',
  [LanguageCode.Kazakh]: 'kk_KZ',
  [LanguageCode.Tajik]: 'tg_TJ',
};

const UPPY_LOAD_ATTEMPTS = 3;
const UPPY_RETRY_DELAY_MS = 3000;

const removeAllFiles = (uppy: Uppy) =>
  uppy.getFiles().forEach((file) => uppy.removeFile(file.id));

const delay = (ms: number) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

type UploadModalLocaleOverride = Record<
  string,
  string | Record<number, string>
>;

// A locale that fails to load leaves Uppy's built-in English strings, it never blocks the upload.
const applyUppyLocale = async (
  uppy: Uppy,
  language: LanguageCode,
  override: UploadModalLocaleOverride,
) => {
  let strings = {};
  try {
    strings = (
      await import(`@uppy/locales/lib/${MAP_LANGUAGE_TO_UPPY_LOCALE[language]}`)
    ).default.strings;
  } catch (e: any) {
    const message = 'Language loading failed: ' + e.message;
    gaUtils.trackException({ fatal: false, description: message });
    eventUtils.trackException({ message });
    try {
      strings = (await import(`@uppy/locales/lib/${DEFAULT_UPPY_LANGUAGE}`))
        .default.strings;
    } catch {
      // Keep the built-in strings.
    }
  }
  uppy.setOptions({ locale: { strings: { ...strings, ...override } } });
};

// idle: nothing asked for the uploader yet; failed: every load attempt failed, the page has to be reloaded
// (after a deploy the old chunks are gone, so retrying in place does not help).
export type ReceiptUploaderStatus = 'idle' | 'loading' | 'ready' | 'failed';

type UseOrderReceiptsInput = {
  orderId: string;
  order: Order | undefined;
  onApiError: (error: AxiosError) => void;
};

export type UseOrderReceiptsResult = {
  receipts: OrderReceipt[] | undefined;
  isReceiptsLoading: boolean;
  canManageReceipts: boolean;
  hasPendingFile: boolean;
  isUploadingReceipt: boolean;
  uploadReceiptModalOpen: boolean;
  setUploadReceiptModalOpen: (open: boolean) => void;
  // Loaded on demand (see receiptUppy.ts): undefined until a receipt upload component calls requestUppy
  // and the load succeeds (uppyStatus 'ready').
  uppy: Uppy | undefined;
  uppyStatus: ReceiptUploaderStatus;
  requestUppy: () => void;
  setReceiptUploadCallbacks: (
    onUpload: () => void,
    onClose: () => void,
  ) => void;
  telegramSupportBotUrl: string | undefined;
  handleTelegramOrderHelp: () => void;
  handleTelegramUploadReceipt: () => void;
};

export const useOrderReceipts = ({
  orderId,
  order,
  onApiError,
}: UseOrderReceiptsInput): UseOrderReceiptsResult => {
  const { t: tUploadReceipt } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.upload_confirmation_receipt',
  });

  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const { data: sciDomainConfig } = useSciDomain();
  const telegramSupportBotUrl = sciDomainConfig?.telegramSupportBotUrl;

  const uploadCallbacksRef = useRef<{
    onUpload: () => void;
    onClose: () => void;
  } | null>(null);

  const [uploadReceiptModalOpen, setUploadReceiptModalOpen] = useState(false);
  const [hasPendingFile, setHasPendingFile] = useState(false);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);

  const canManageReceipts = useMemo(
    () =>
      [OrderStatus.Cancelled, OrderStatus.Dispute].includes(
        order?.status as OrderStatus,
      ) && !!order?.requisites,
    [order],
  );

  // The receipt upload components (the upload dialog, the SQ receipts step) call requestUppy when they mount,
  // and they are mounted only where a receipt can be added, so the uploader loads for exactly those screens.
  const [uppyRequested, setUppyRequested] = useState(false);
  const requestUppy = useCallback(() => setUppyRequested(true), []);
  const [uppy, setUppy] = useState<Uppy>();
  const [uppyStatus, setUppyStatus] = useState<ReceiptUploaderStatus>('idle');

  const uploadModalLocaleOverride = useMemo(
    () => ({
      dropPasteImportFiles: tUploadReceipt('description'),
      uploadXFiles: {
        0: tUploadReceipt('submit'),
      },
    }),
    [tUploadReceipt],
  );

  // Read by the load below, so a language change while loading does not restart it.
  const localeRef = useRef({ language, uploadModalLocaleOverride });
  localeRef.current = { language, uploadModalLocaleOverride };
  // The locale the uploader already has, so the language effect below does not re-apply it after the load.
  const appliedLocaleRef = useRef<typeof localeRef.current | null>(null);

  useEffect(() => {
    if (!uppyRequested) {
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      setUppyStatus('loading');
      let lastError: Error | undefined;
      for (let attempt = 1; attempt <= UPPY_LOAD_ATTEMPTS; attempt += 1) {
        try {
          const loaded = await loadReceiptUppy();
          // The locale goes in before the uploader is shown, so the dialog never opens in English first.
          const locale = localeRef.current;
          await applyUppyLocale(
            loaded,
            locale.language,
            locale.uploadModalLocaleOverride,
          );
          if (!cancelled) {
            // The uploader is one per app (receiptUppy.ts), so a file left from an earlier mount goes now,
            // before the receipt components get it and add a file dropped while it loaded (SQ).
            removeAllFiles(loaded);
            appliedLocaleRef.current = locale;
            setUppy(loaded);
            setUppyStatus('ready');
          }
          return;
        } catch (error) {
          lastError = error as Error;
          // Only the final failure is an exception, so a flaky network does not raise up to three per customer.
          if (attempt < UPPY_LOAD_ATTEMPTS) {
            eventUtils.trackEvent(
              {
                event: EventName.ReceiptUploaderLoadRetry,
                message: `Attempt ${attempt}: ${lastError.message}`,
              },
              { ga: true, app: false },
            );
            await delay(UPPY_RETRY_DELAY_MS);
          }
          if (cancelled) {
            return;
          }
        }
      }
      eventUtils.trackException({
        message: `Receipt uploader failed to load after ${UPPY_LOAD_ATTEMPTS} attempts: ${lastError?.message}`,
      });
      setUppyStatus('failed');
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [uppyRequested]);

  useEffect(() => {
    if (!uppy) {
      return undefined;
    }
    const onFileAdded = () => setHasPendingFile(true);
    const onFileRemoved = () => setHasPendingFile(uppy.getFiles().length > 0);
    const onUploadStart = () => setIsUploadingReceipt(true);

    uppy.on('file-added', onFileAdded);
    uppy.on('file-removed', onFileRemoved);
    uppy.on('upload', onUploadStart);
    // Child effects run first, so a file dropped while loading (SQ) can already be in before these listeners.
    setHasPendingFile(uppy.getFiles().length > 0);

    return () => {
      uppy.off('file-added', onFileAdded);
      uppy.off('file-removed', onFileRemoved);
      uppy.off('upload', onUploadStart);
    };
  }, [uppy]);

  // Clears the receipt when the order leaves cancelled/dispute. Not on the uploader load: the SQ receipt step
  // runs with canManageReceipts false and its file, dropped while loading, is added right then.
  const canManageReceiptsRef = useRef(canManageReceipts);
  const uppyRef = useRef(uppy);
  uppyRef.current = uppy;
  useEffect(() => {
    const couldManageReceipts = canManageReceiptsRef.current;
    canManageReceiptsRef.current = canManageReceipts;
    if (couldManageReceipts && !canManageReceipts && uppyRef.current) {
      removeAllFiles(uppyRef.current);
    }
  }, [canManageReceipts]);

  const { data: receipts, isLoading: isReceiptsLoading } = useQuery(
    QueryKey.OrderReceipts,
    () => ordersApi.getOrderReceipts(orderId),
    {
      enabled: canManageReceipts,
      retry: false,
      onError: onApiError,
    },
  );

  const { mutate: uploadPaymentReceipt } = useMutation(ordersApi.uploadReceipt);

  useEffect(() => {
    if (!uppy) {
      return undefined;
    }
    const handleComplete = (result: UploadResult<Record<string, unknown>>) => {
      if (!uploadCallbacksRef.current) {
        return;
      }

      const file = result.successful[0];
      const formData = new FormData();
      formData.append('file', file.data, file.name);

      const { onUpload, onClose } = uploadCallbacksRef.current;

      uploadPaymentReceipt(
        { orderId, body: formData },
        {
          onSuccess: () => {
            eventUtils.trackEvent({ event: EventName.ReceiptUpload });
            queryClient.invalidateQueries(QueryKey.OrderReceipts);
            removeAllFiles(uppy);
            onUpload();
            onClose();
            setIsUploadingReceipt(false);
          },
          onError: (e: unknown) => {
            const error = e as AxiosError<{ message: string | undefined }>;
            const message = error?.response?.data?.message || error?.message;
            eventUtils.trackEvent({
              event: EventName.ReceiptUploadError,
              message,
            });
            uppy.info(message, 'error');
            uppy.resetProgress();
            setIsUploadingReceipt(false);
          },
        },
      );
    };

    uppy.on('complete', handleComplete);
    return () => {
      uppy.off('complete', handleComplete);
    };
  }, [uppy, orderId, uploadPaymentReceipt, queryClient]);

  // Language switched after the uploader loaded; the locale the load applied is not applied again.
  useEffect(() => {
    const applied = appliedLocaleRef.current;
    if (
      !uppy ||
      (applied?.language === language &&
        applied.uploadModalLocaleOverride === uploadModalLocaleOverride)
    ) {
      return;
    }
    appliedLocaleRef.current = { language, uploadModalLocaleOverride };
    void applyUppyLocale(uppy, language, uploadModalLocaleOverride);
  }, [uppy, language, uploadModalLocaleOverride]);

  const setReceiptUploadCallbacks = useCallback(
    (onUpload: () => void, onClose: () => void) => {
      uploadCallbacksRef.current = { onUpload, onClose };
    },
    [],
  );

  const handleTelegramOrderHelp = useCallback(() => {
    gaUtils.trackEvent(EventName.TelegramHelp);
    window.open(`${telegramSupportBotUrl}?start=order_${orderId}`, '_blank');
  }, [orderId, telegramSupportBotUrl]);

  const handleTelegramUploadReceipt = useCallback(() => {
    gaUtils.trackEvent(EventName.TelegramUploadReceipt);
    window.open(`${telegramSupportBotUrl}?start=receipt`, '_blank');
  }, [telegramSupportBotUrl]);

  return {
    receipts,
    isReceiptsLoading,
    canManageReceipts,
    hasPendingFile,
    isUploadingReceipt,
    uploadReceiptModalOpen,
    setUploadReceiptModalOpen,
    uppy,
    uppyStatus,
    requestUppy,
    setReceiptUploadCallbacks,
    telegramSupportBotUrl,
    handleTelegramOrderHelp,
    handleTelegramUploadReceipt,
  };
};
